import { MediumParams } from 'src/modules/user-input/components/MediumParams.tsx';
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { reducer } from 'src/modules/user-input/calcV100/reducer.ts';
import { DecimalField } from 'src/modules/common/components/DecimalField/DecimalField.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { UnitSelector } from 'src/modules/user-input/components/UnitSelector.tsx';
import { targetUnits, targetUnitsAm } from 'src/modules/user-input/targetUnits.ts';
import { initialState } from 'src/modules/user-input/calcV100/initialState.ts';
import { MediumParamsChangeEventDetails } from 'src/modules/user-input/MediumParams/types.ts';
import { calcV100 } from 'src/modules/user-input/calcV100/calcV100.ts';
import { convert } from 'src/modules/user-input/convert.ts';
import { CalculatorModalLayout } from 'src/modules/common/layouts/CalculatorModalLayout.tsx';
import {
    CalculatorModalTopSection,
} from 'src/modules/common/components/CalculatorModalTopSection/CalculatorModalTopSection';
import {
    CalculatorModalMainSection,
} from 'src/modules/common/components/CalculatorModalMainSection/CalculatorModalMainSection.tsx';
import { Units } from 'uom-units';
import { RegionsEnum } from 'src/generated/operations.ts';
import { calcV100MethodB } from 'src/modules/user-input/calcV100/calcV100MethodB.ts';
import { CalculationMethod } from 'src/modules/user-input/components/CalculationMethod.tsx';
import { numberFormatter } from 'src/utils/numberFormatter.ts';
import { Modal } from 'src/modules/common/components/Modal/Modal.tsx';
import { ModalHeader } from 'src/modules/common/components/Modal/ModalHeader.tsx';
import { ModalBody } from 'src/modules/common/components/Modal/ModalBody.tsx';
import { ModalFooter } from 'src/modules/common/components/Modal/ModalFooter.tsx';
import { AllowedUnits } from 'src/modules/user-input/calcV100/unitsMapping';
import { ConfigContext } from 'src/configContext';
import { I18n } from 'src/modules/common/components/I18n/I18n';
import { Button } from 'src/modules/common/components/Button/Button';
import { ModalProps } from 'src/modules/user-input/types';

type Props = ModalProps & {
    unitSelectionOptions: AllowedUnits[keyof AllowedUnits];
    uuid: string;
}

export const CalcV100 = ({
    opened,
    title,
    titleResult,
    description,
    allowedUnits,
    unitSelectionOptions,
    targetUnit,
    uuid,
    handleModalClose,
    handleModalTransfer,
}: Props) => {
    const { userSettings: { currentLocale, currentRegion } } = useContext(ConfigContext);

    const [value, setValue] = useState('');
    const [unit, setUnit] = useState(targetUnit);

    const mediumParamsRef = useRef({ reset: () => null });

    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        ratedCapacityUnit: allowedUnits.ratedCapacity[0],
        temperatureDifferenceAirUnit: allowedUnits.temperatureDifference[0],
        temperatureDifferenceUnit: allowedUnits.temperatureDifference[1],
    });
    const [isMethodA, setMethod] = useState<boolean>(true);

    const onMediumParamsChange = useCallback((mediumParams: MediumParamsChangeEventDetails) => {
        dispatch({
            type: 'mediumParams_change',
            payload: mediumParams,
        });
    }, [dispatch]);

    const v100 = useMemo(() => {
        if (isMethodA) {
            return convert(
                calcV100(
                    convert(
                        state.ratedCapacity,
                        state.ratedCapacityUnit,
                        targetUnits.ratedCapacity,
                    ),
                    convert(
                        state.temperatureDifference,
                        state.temperatureDifferenceUnit,
                        targetUnits.temperatureDifference,
                    ),
                    state.heatCapacity,
                    state.density,
                ),
                Units.CubicMeterPerHour,
                unit,
            );
        }
        return convert(
            calcV100MethodB(
                convert(
                    state.airflowVolume,
                    state.airflowVolumeUnit,
                    targetUnitsAm.volumetricFlowAir,
                ),
                state.temperatureDifferenceAir,
                state.temperatureDifference,
                state.heatCapacity,
                state.density,
            ),
            Units.GallonsPerMinute,
            unit,
        );
    }, [state, unit, isMethodA]);

    const handleReset = useCallback(() => {
        dispatch({ type: 'reset' });
        mediumParamsRef.current.reset();
    }, []);

    useEffect(() => {
        setValue(v100);
    }, [v100]);

    useEffect(() => {
        setUnit(targetUnit);
    }, [targetUnit]);

    return (
        <Modal
            opened={opened}
            handleClose={handleModalClose}
        >
            <ModalHeader
                handleClose={handleModalClose}
                handleReset={handleReset}
                title={title}
            >
                <I18n messageId={description}/>
            </ModalHeader>

            <ModalBody>
                <CalculatorModalLayout>
                    <CalculatorModalTopSection>
                        <MediumParams
                            uuid={uuid}
                            currentRegion={currentRegion}
                            allowedUnits={allowedUnits}
                            currentLocale={currentLocale}
                            onChange={onMediumParamsChange}
                            mediumParamsRef={mediumParamsRef}
                        />
                        {currentRegion === RegionsEnum.Am && (
                            <CalculationMethod isMethodA={isMethodA} setMethod={setMethod}/>
                        )}
                    </CalculatorModalTopSection>

                    <CalculatorModalMainSection
                        title={titleResult}
                        result={numberFormatter(v100, currentLocale)}
                        selectedUnit={unit}
                        allowedUnits={unitSelectionOptions}
                        onUnitChange={setUnit}
                    >
                        {isMethodA && (
                            <>
                                <DecimalField
                                    name="ratedCapacity"
                                    inputSize="md"
                                    labelText={MESSAGES.RATED_CAP}
                                    value={state.ratedCapacity}
                                    endAdornment={(
                                        <UnitSelector
                                            selected={state.ratedCapacityUnit.name}
                                            values={allowedUnits.ratedCapacity}
                                            onChange={(payload) => {
                                                dispatch({ type: 'ratedCapacity_unit', payload });
                                            }}
                                        />
                                    )}
                                    currentLocale={currentLocale}
                                    onValueChange={(payload) => {
                                        dispatch({
                                            type: 'ratedCapacity_change',
                                            payload,
                                        });
                                    }}
                                    autoComplete="off"
                                />
                                <DecimalField
                                    name="temperatureDifference"
                                    inputSize="md"
                                    labelText={MESSAGES.TEMP_DIFF}
                                    value={state.temperatureDifference}
                                    endAdornment={(
                                        <UnitSelector
                                            selected={state.temperatureDifferenceUnit.name}
                                            values={[Units.Kelvin]}
                                            onChange={(payload) => {
                                                dispatch({ type: 'temperatureDifference_unit', payload });
                                            }}
                                        />
                                    )}
                                    currentLocale={currentLocale}
                                    onValueChange={(payload) => {
                                        dispatch({
                                            type: 'temperatureDifference_change',
                                            payload,
                                        });
                                    }}
                                    autoComplete="off"
                                />
                            </>
                        )}

                        {!isMethodA && (
                            <>
                                <DecimalField
                                    name="ratedCapacity"
                                    inputSize="md"
                                    labelText={MESSAGES.airflowVolume}
                                    value={state.airflowVolume}
                                    endAdornment={(
                                        <UnitSelector
                                            selected={state.airflowVolumeUnit.name}
                                            values={allowedUnits.airflowVolume}
                                            onChange={(payload) => {
                                                dispatch({ type: 'airflowVolume_unit', payload });
                                            }}
                                        />
                                    )}
                                    currentLocale={currentLocale}
                                    onValueChange={(payload) => {
                                        dispatch({
                                            type: 'airflowVolume_change',
                                            payload,
                                        });
                                    }}
                                    autoComplete="off"
                                />
                                <DecimalField
                                    name="temperatureDifferenceAir"
                                    inputSize="md"
                                    labelText={MESSAGES.TEMP_DIFF_AIR}
                                    value={state.temperatureDifferenceAir}
                                    endAdornment={(
                                        <UnitSelector
                                            selected={state.temperatureDifferenceAirUnit.name}
                                            values={allowedUnits.temperatureDifference}
                                            onChange={(payload) => {
                                                dispatch({ type: 'temperatureDifferenceAir_unit', payload });
                                            }}
                                        />
                                    )}
                                    currentLocale={currentLocale}
                                    onValueChange={(payload) => {
                                        dispatch({
                                            type: 'temperatureDifferenceAir_change',
                                            payload,
                                        });
                                    }}
                                    autoComplete="off"
                                />
                                <DecimalField
                                    name="temperatureDifference"
                                    inputSize="md"
                                    labelText={MESSAGES.TEMP_DIFF_WATER}
                                    value={state.temperatureDifference}
                                    endAdornment={(
                                        <UnitSelector
                                            selected={state.temperatureDifferenceUnit.name}
                                            values={allowedUnits.temperatureDifference}
                                            onChange={(payload) => {
                                                dispatch({ type: 'temperatureDifference_unit', payload });
                                            }}
                                        />
                                    )}
                                    currentLocale={currentLocale}
                                    onValueChange={(payload) => {
                                        dispatch({
                                            type: 'temperatureDifference_change',
                                            payload,
                                        });
                                    }}
                                    autoComplete="off"
                                />
                            </>
                        )}
                    </CalculatorModalMainSection>
                </CalculatorModalLayout>
            </ModalBody>

            <ModalFooter>
                <Button
                    kind="secondary"
                    fullWidth={true}
                    onClick={handleModalClose}
                >
                    <I18n messageId={MESSAGES.CANCEL}/>
                </Button>
                <Button
                    fullWidth={true}
                    onClick={() => {
                        handleModalTransfer({
                            value,
                            unit,
                        });
                    }}
                >
                    <I18n messageId={MESSAGES.apply}/>
                </Button>
            </ModalFooter>
        </Modal>
    );
};
